import { getMarketCode } from './market';

const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production';

const resolveHost = () => {
  const marketCode = getMarketCode();
  if (isProduction) {
    if (marketCode === 'NO') return 'https://cdn.drive.no';
    return 'https://cdn.wayke.se';
  }
  if (marketCode === 'NO') return 'https://test-cdn.drive.no';
  return 'https://test-cdn.wayketech.se';
};

/**
 * Converts a 32-character string into a GUID format by inserting dashes.
 * Throws an error if the input string is not exactly 32 characters.
 */
const toGuid = (str: string): string => {
  if (str.length !== 32) {
    throw new Error(`Invalid GUID format: ${str}`);
  }
  return str.replace(/^(.{8})(.{4})(.{4})(.{4})(.{12})$/, '$1-$2-$3-$4-$5');
};

/**
 * Extracts and converts the last two nonempty segments of a URL path
 * into GUIDs. Throws an error if there aren’t at least two segments.
 */
const resolveIds = (input?: string): { id: string; mediaId: string } | undefined => {
  if (!input) return undefined;
  const parts = input.split('/').filter(Boolean);
  if (parts.length < 2) {
    throw new Error('Invalid GUID format');
  }
  const mediaId = parts[parts.length - 2];
  const id = parts[parts.length - 1];
  return {
    id: toGuid(id),
    mediaId: toGuid(mediaId),
  };
};

export type CfitWidth =
  | 48
  | 64
  | 96
  | 128
  | 225
  | 375
  | 380
  | 450
  | 600
  | 750
  | 770
  | 800
  | 840
  | 900
  | 1170
  | 1200;

type CfitFit = 'crop' | 'contain';

interface Options {
  width: CfitWidth;
  cacheKey?: string;
  height?: number;
  quality?: number;
  fallback?: string;
  fit?: CfitFit;
  format?: 'jpeg' | 'webp';
}

const buildParams = (
  { width, height, quality, cacheKey, fit, format = 'webp' }: Options,
  revision?: string,
) => {
  const params = new URLSearchParams({ format, w: String(width) });
  if (revision) params.set('r', revision);
  if (cacheKey) params.set('cacheKey', cacheKey);
  if (height) params.set('h', String(height));
  if (quality) params.set('q', String(quality));
  if (fit) params.set('fit', String(fit));
  params.sort();
  return params;
};

export const urlToCfit = (url: string | undefined, options: Options) => {
  if (!url) return undefined;
  const host = resolveHost();

  try {
    // /content should not go thorugh cfit
    if (
      [
        'https://test-cdn.wayketech.se/content',
        'https://cdn.wayke.se/content',
        'https://test-cdn.drive.no/content',
        'https://cdn.drive.no/content',
      ].some((x) => url.startsWith(x))
    ) {
      const newUrl = new URL(url);
      const params = new URLSearchParams();

      if (options.width) params.set('w', String(options.width));
      if (options.height) params.set('h', String(options.height));
      params.sort();
      newUrl.search = params.toString();
      return newUrl.toString();
    }

    // If the URL already has "/cfit/", update its query params and ensure the host matches.
    if (url.includes('/cfit/')) {
      const parsedUrl = new URL(url);
      const revision = new URLSearchParams(parsedUrl.search).get('r') || undefined;
      const newSearch = buildParams(options, revision).toString();

      // Check if the current URL's origin is different from the resolved host.
      if (parsedUrl.origin !== host) {
        // Rebuild the URL using the resolved host, preserving the pathname and updated search params.
        return `${host}${parsedUrl.pathname}?${newSearch}`;
      }

      parsedUrl.search = newSearch;
      return parsedUrl.toString();
    }

    // Otherwise, convert the GUID in the URL to a cfit URL.
    const converted = resolveIds(url);
    if (!converted) throw new Error('Invalid GUID format');

    const params = buildParams(options);
    return `${host}/cfit/${converted.mediaId}/${converted.id}?${params}`;
  } catch {
    return options.fallback;
  }
};
